import { useEffect, useState, useCallback, useRef } from 'react';
import { Storage } from '../constants/storage';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
    authApi,
    authStorage,
    isAuthenticated,
    getApiErrorMessage,
    GOOGLE_CLIENT_ID,
    APPLE_CLIENT_ID,
    APPLE_REDIRECT_URI,
    APPLE_CLIENT,
} from '../utils/api';
import SimplizeLogo from './SimplizeLogo';
import SimplizeOriginalLogo from './SimplizeOriginalLogo';

const loadScript = (src, id) => {
    return new Promise((resolve, reject) => {
        const existing = document.getElementById(id) || document.querySelector(`script[src="${src}"]`);
        if (existing) {
            if (existing.dataset.loaded === 'true') {
                resolve();
                return;
            }
            existing.addEventListener('load', () => resolve());
            existing.addEventListener('error', reject);
            return;
        }
        const script = document.createElement('script');
        script.id = id;
        script.src = src;
        script.async = true;
        script.defer = true;
        script.onload = () => { script.dataset.loaded = 'true'; resolve(); };
        script.onerror = reject;
        document.head.appendChild(script);
    });
};

const loadGoogleSDK = () => loadScript('https://accounts.google.com/gsi/client', 'google-gsi');

const loadAppleSDK = () => loadScript('https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js', 'apple-jssdk');

const GoogleIcon = () => (
    <svg viewBox="0 0 24 24" className="w-5 h-5">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
);

const AppleIcon = () => (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#000">
        <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
);

const PROVIDERS = [
    { id: 'google', label: 'Google', icon: <GoogleIcon /> },
    { id: 'apple', label: 'Apple', icon: <AppleIcon /> },
];

export default function RegisterPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const returnUrl = '/onboarding';
    
    const [email, setEmail] = useState('');
    const [fullName, setFullName] = useState('');
    const [phone, setPhone] = useState('');
    const [password, setPassword] = useState('');
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [agreed, setAgreed] = useState(false);
    const [otpType, setOtpType] = useState('sms'); // 'sms' | 'email'
    const [showPassword, setShowPassword] = useState(false);
    const otpRefs = useRef([]);
    
    const [step, setStep] = useState('email'); // 'email' | 'details' | 'otp'
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [timer, setTimer] = useState(98); // 01:38 = 98s
    const [sdkReady, setSdkReady] = useState({ google: false, apple: false });

    useEffect(() => {
        loadGoogleSDK()
            .then(() => setSdkReady((s) => ({ ...s, google: true })))
            .catch(() => { });

        if (APPLE_CLIENT_ID) {
            loadAppleSDK()
                .then(() => setSdkReady((s) => ({ ...s, apple: true })))
                .catch(() => { });
        }
    }, []);

    useEffect(() => {
        if (isAuthenticated()) {
            navigate('/', { replace: true });
        }

        // Handle social login callback
        const token = searchParams.get('token');
        const errorParam = searchParams.get('error');

        if (token) {
            authStorage.set({
                accessToken: token,
                // Expired time is a fallback, will be updated by refresh flow if needed
                expiredTime: Date.now() + 15 * 60_000,
            });
            try {
                localStorage.setItem(Storage.access_token, token);
                localStorage.setItem(Storage.expired_time, String(Date.now() + 15 * 60_000));
            } catch (e) {
                console.error('Failed to save token to localStorage', e);
            }

            (async () => {
                try {
                    await authApi.activeFreeTrial();
                } catch (e) {
                    console.error('activeFreeTrial failed', e);
                }
                // Redirect to destination
                window.location.replace(returnUrl || '/');
            })();
        }

        if (errorParam) {
            setError('Đăng ký bằng mạng xã hội không thành công. Vui lòng thử lại.');
        }
    }, [navigate, searchParams, returnUrl]);

    useEffect(() => {
        let interval;
        if (step === 'otp' && timer > 0) {
            interval = setInterval(() => setTimer(t => t - 1), 1000);
        }
        return () => clearInterval(interval);
    }, [step, timer]);

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    };

    const handleEmailContinue = (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setError('');
        setStep('details');
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        if (!agreed || isSubmitting) return;
        setError('');
        setIsSubmitting(true);
        try {
            const resp = await authApi.register(email.trim(), fullName.trim(), password, phone.trim());
            const data = resp?.data || resp;
            if (data?.requiredVerifyOtp) {
                setOtpType('sms');
                setStep('otp');
            } else {
                setOtpType('email');
                setStep('otp');
            }
            setTimer(98);
        } catch (err) {
            setError(getApiErrorMessage(err, 'Đã có lỗi xảy ra. Vui lòng thử lại sau.'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        const otpValue = otp.join('');
        if (isSubmitting || otpValue.length < 6) return;
        setError('');
        setIsSubmitting(true);
        try {
            let data;
            if (otpType === 'sms') {
                data = await authApi.verifyOtp({
                    email,
                    fullName,
                    otp: otpValue,
                    password,
                    phoneNumber: phone,
                    referralCode: ''
                });
            } else {
                data = await authApi.verifyEmailOtp(email, otpValue);
            }
            
            if (data?.accessToken) {
                try {
                    await authApi.activeFreeTrial();
                } catch (e) {
                    console.error('activeFreeTrial failed', e);
                }
                window.location.replace(returnUrl || '/');
            } else {
                setError('Mã xác thực không đúng');
            }
        } catch (err) {
            setError(getApiErrorMessage(err, 'Đã có lỗi xảy ra. Vui lòng thử lại sau.'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOtpChange = (index, value) => {
        const char = value.replace(/\D/g, '').slice(-1);
        const newOtp = [...otp];
        newOtp[index] = char;
        setOtp(newOtp);

        if (char && index < 5) {
            otpRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
    };

    const handleResendOtp = async () => {
        if (timer > 0) return;
        try {
            if (otpType === 'sms') {
                await authApi.resendOtp({
                    email,
                    fullName,
                    password,
                    phoneNumber: phone
                });
            } else {
                await authApi.resendEmailOtp(email);
            }
            setTimer(98);
        } catch (err) {
            setError(getApiErrorMessage(err, 'Đã có lỗi xảy ra. Vui lòng thử lại sau.'));
        }
    };

    const handleSocialSuccess = (data) => {
        if (data?.accessToken) {
            window.location.replace(returnUrl || '/');
        }
    };

    const handleGoogleLogin = () => {
        if (!sdkReady.google) return;
        setError('');
        try {
            const client = window.google.accounts.oauth2.initCodeClient({
                client_id: GOOGLE_CLIENT_ID,
                scope: 'openid email profile',
                ux_mode: 'popup',
                callback: async (response) => {
                    if (!response?.code) return;
                    try {
                        const data = await authApi.socialLogin('google', response.code, '', { tickerPdf: '' });
                        handleSocialSuccess(data);
                    } catch (err) {
                        setError(getApiErrorMessage(err, 'Đăng nhập Google thất bại'));
                    }
                },
            });
            client.requestCode();
        } catch {
            setError('Không khởi tạo được Google Login');
        }
    };

    const handleAppleLogin = async () => {
        if (!sdkReady.apple || !window.AppleID) return;
        setError('');
        try {
            window.AppleID.auth.init({
                clientId: APPLE_CLIENT_ID,
                scope: 'name email',
                redirectURI: APPLE_REDIRECT_URI,
                usePopup: true,
            });
            const response = await window.AppleID.auth.signIn();
            const code = response?.authorization?.code;
            if (!code) return;
            const userName = response?.user?.name;
            const name = userName ? [userName.firstName, userName.lastName].filter(Boolean).join(' ') : '';
            const data = await authApi.socialLoginRaw({
                provider: 'apple',
                client: APPLE_CLIENT || undefined,
                code,
                name: name || undefined,
                referralCode: '',
                property: { tickerPdf: '' },
            });
            handleSocialSuccess(data);
        } catch (err) {
            if (err?.error !== 'popup_closed_by_user') {
                setError(getApiErrorMessage(err, 'Đăng nhập Apple thất bại'));
            }
        }
    };

    const handleSocialLogin = (provider) => {
        if (provider === 'google') return handleGoogleLogin();
        if (provider === 'apple') return handleAppleLogin();
    };

    return (
        <div className="fixed inset-0 z-50 flex flex-col overflow-y-auto" style={{ background: '#f8f8f7' }}>
            <div className="flex-1 flex flex-col items-center justify-center px-4 py-10">
                <SimplizeLogo className="h-[35px] w-auto mb-[32px]" />
                <div className="w-[404px] border border-[#FFFFFF] bg-white rounded-[16px] p-[24px_32px] shadow-[0_0_20px_rgba(0,0,0,0.05)]">
                    
                    {step === 'email' && (
                        <>
                            <div className="flex flex-col items-center mb-[32px]">
                                <h1 className="text-[28px] leading-[36px] font-bold text-[#111827] text-center mb-[8px]">
                                    Tạo tài khoản miễn phí
                                </h1>
                                <p className="text-[16px] text-[#a0a0a5] text-center  whitespace-nowrap">
                                    Bắt đầu tham gia thị trường tài chính cùng Nebula
                                </p>
                            </div>

                            <div className="space-y-[16px]">
                                {PROVIDERS.map((provider) => (
                                    <button
                                        key={provider.id}
                                        type="button"
                                        onClick={() => handleSocialLogin(provider.id)}
                                        className="w-full flex items-center h-[48px] px-[16px] rounded-[12px] border-[0.5px] border-[#e8e8e8] bg-white text-[16px] font-medium text-[#111827] shadow-[0_0_20px_rgba(0,0,0,0.05)] hover:opacity-80 active:opacity-70 transition-opacity cursor-pointer"
                                    >
                                        <span className="w-[20px] flex justify-center shrink-0">{provider.icon}</span>
                                        <span className="flex-1 text-center pr-[20px]">Tiếp tục với {provider.label}</span>
                                    </button>
                                ))}
                            </div>

                            <div className="flex items-center gap-4 my-[16px]">
                                <div className="flex-1 h-px bg-[#ececed]" />
                                <span className="text-[13px] text-[#a0a0a5]">hoặc</span>
                                <div className="flex-1 h-px bg-[#ececed]" />
                            </div>

                            <form onSubmit={handleEmailContinue} className="space-y-[16px]">
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Nhập địa chỉ email của bạn..."
                                    className="w-full h-[48px] px-[16px] rounded-[12px] border border-[#ECECED] bg-white text-[16px] text-[#111827] focus:outline-none focus:border-[#999] placeholder:text-[#a0a0a5] transition-colors"
                                    required
                                />
                                <button
                                    type="submit"
                                    className="w-full h-[48px] rounded-[12px] bg-[#111827] text-white text-[16px] font-medium hover:bg-[#333] active:bg-[#000] transition-colors cursor-pointer"
                                >
                                    Tiếp tục
                                </button>
                            </form>
                        </>
                    )}

                    {step === 'details' && (
                        <>
                            <div className="flex flex-col items-center mb-[32px]">
                                <h1 className="text-[28px] leading-[36px] font-bold text-[#111827] text-center mb-[8px]">
                                    Tạo tài khoản miễn phí
                                </h1>
                                <p className="text-[16px] text-[#636366] text-center whitespace-nowrap">
                                    Bắt đầu tham gia thị trường tài chính cùng Nebula
                                </p>
                            </div>

                            <form onSubmit={handleRegister}>
                                <div className="space-y-[16px]">
                                    {/* Email Field - Read Only */}
                                    <div 
                                        className="w-full h-[48px] px-4 rounded-[12px] border border-[#ECECED] bg-[#f5f5f5] flex items-center justify-between cursor-pointer group"
                                        onClick={() => setStep('email')}
                                    >
                                        <span className="text-[16px] text-[#111827]">{email}</span>
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="opacity-60 group-hover:opacity-100 transition-opacity">
                                            <path d="M4 17.5V20H6.5L13.84 12.66L11.34 10.16L4 17.5ZM15.77 10.73C16.03 10.47 16.03 10.05 15.77 9.79L14.21 8.23C13.95 7.97 13.53 7.97 13.27 8.23L12.05 9.45L14.55 11.95L15.77 10.73V10.73Z" fill="#111827"/>
                                        </svg>
                                    </div>

                                    {/* Full Name Input */}
                                    <div className="w-full h-[48px] px-4 rounded-[12px] border border-[#ECECED] bg-white flex items-center focus-within:border-[#111827] transition-colors">
                                        <input
                                            type="text"
                                            value={fullName}
                                            onChange={(e) => setFullName(e.target.value)}
                                            placeholder="Tên của bạn là..."
                                            className="flex-1 bg-transparent text-[16px] text-[#111827] focus:outline-none placeholder:text-[#a0a0a5]"
                                            required
                                        />
                                    </div>

                                    {/* Phone Input */}
                                    <div className="relative flex items-center">
                                        <div className="absolute left-4 flex items-center gap-4">
                                            <div className="flex items-center gap-2">
                                                <svg width="16" height="10" viewBox="0 0 16 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <rect width="16" height="10" fill="#D95656"/>
                                                    <path d="M8 2L8.58779 3.80902H10.4894L8.95081 4.92705L9.53859 6.73607L8 5.61803L6.46141 6.73607L7.04919 4.92705L5.51057 3.80902H7.41221L8 2Z" fill="#FBBF24"/>
                                                </svg>
                                                <span className="text-[14px] text-[#111827] font-medium">+84</span>
                                            </div>
                                            <div className="w-[1px] h-[24px] bg-[#ECECED]"></div>
                                        </div>
                                        <input
                                            type="tel"
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value)}
                                            placeholder="Nhập số điện thoại..."
                                            className="w-full h-[48px] pl-[92px] pr-4 rounded-[12px] border border-[#ECECED] bg-white text-[16px] text-[#111827] focus:outline-none focus:border-[#111827] placeholder:text-[#a0a0a5] transition-colors"
                                            required
                                        />
                                    </div>

                                    {/* Password Input */}
                                    <div className="relative">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="Nhập mật khẩu..."
                                            className="w-full h-[48px] px-4 rounded-[12px] border border-[#ECECED] bg-white text-[16px] text-[#111827] focus:outline-none focus:border-[#111827] placeholder:text-[#a0a0a5] transition-colors"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-[#636366]"
                                        >
                                            {showPassword ? (
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                                            ) : (
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                <div className="mt-6 flex items-start gap-2">
                                    <div 
                                        className={`mt-1 w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center cursor-pointer transition-colors ${agreed ? 'bg-[#111827] border-[#111827]' : 'border-[#ECECED] bg-white'}`}
                                        onClick={() => setAgreed(!agreed)}
                                    >
                                        {agreed && (
                                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="20 6 9 17 4 12"></polyline>
                                            </svg>
                                        )}
                                    </div>
                                    <label className="text-[13px] text-[#636366] leading-relaxed cursor-pointer" onClick={() => setAgreed(!agreed)}>
                                        Tôi đã đọc và đồng ý với <span className="text-[#111827] font-medium underline">Điều khoản sử dụng</span> của Nebula.
                                    </label>
                                </div>

                                {error && <p className="text-xs text-red-500 mt-2">{error}</p>}

                                <button
                                    type="submit"
                                    disabled={isSubmitting || !agreed || !phone || !password || !fullName}
                                    className={`w-full h-[48px] mt-8 rounded-[12px] text-white text-[16px] font-bold transition-colors cursor-pointer ${
                                        isSubmitting || !agreed || !phone || !password || !fullName ? 'bg-[#999]' : 'bg-[#111827] hover:bg-[#000]'
                                    }`}
                                >
                                    {isSubmitting ? 'Đang xử lý...' : 'Đăng ký'}
                                </button>
                            </form>
                        </>
                    )}

                    {step === 'otp' && (
                        <>
                            <div className="flex flex-col items-center mb-[24px]">
                                <h1 className="text-[28px] leading-[36px] font-bold text-[#111827] text-center mb-[8px]">
                                    Xác thực OTP
                                </h1>
                                <p className="text-[16px] text-[#636366] text-center">
                                    Nhập mã xác nhận được gửi đến số điện thoại:
                                </p>
                                <p className="text-[16px] text-[#111827] font-bold text-center">
                                    {phone}
                                </p>
                            </div>

                            <form onSubmit={handleVerifyOtp}>
                                <div className="flex flex-col items-center">
                                    <p className="text-[14px] text-[#636366] mb-[24px]">Vui lòng nhập mã OTP bên dưới</p>
                                    
                                    <div className="flex justify-between w-full gap-2 mb-[32px]">
                                        {otp.map((digit, index) => (
                                            <input
                                                key={index}
                                                ref={el => otpRefs.current[index] = el}
                                                type="text"
                                                inputMode="numeric"
                                                maxLength={1}
                                                value={digit}
                                                onChange={(e) => handleOtpChange(index, e.target.value)}
                                                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                                className="w-[50px] h-[64px] text-center text-2xl font-bold rounded-[8px] border border-[#ECECED] bg-white text-[#111827] focus:outline-none focus:border-[#111827] transition-colors"
                                            />
                                        ))}
                                    </div>
                                    
                                    <div className="flex flex-col items-center gap-1 mb-[32px]">
                                        <p className="text-[14px] text-[#636366]">Mã có hiệu lực trong <span className="text-[#111827]">{formatTime(timer)}</span></p>
                                        <button
                                            type="button"
                                            onClick={handleResendOtp}
                                            disabled={timer > 0}
                                            className={`text-[14px] font-bold transition-colors ${
                                                timer > 0 ? 'text-[#ccc] cursor-not-allowed' : 'text-[#111827] hover:underline cursor-pointer'
                                            }`}
                                        >
                                            Gửi lại mã
                                        </button>
                                    </div>
                                </div>

                                {error && <p className="text-xs text-red-500 mb-4 text-center">{error}</p>}

                                <button
                                    type="submit"
                                    disabled={isSubmitting || otp.join('').length < 6}
                                    className={`w-full h-[48px] rounded-[12px] text-white text-[16px] font-bold transition-colors cursor-pointer ${
                                        isSubmitting || otp.join('').length < 6 ? 'bg-[#999]' : 'bg-[#111827] hover:bg-[#000]'
                                    }`}
                                >
                                    {isSubmitting ? 'Đang xác nhận...' : 'Xác nhận'}
                                </button>
                            </form>
                        </>
                    )}

                    <div className="mt-[18px] text-center">
                        <span className="text-[14px] text-[#636366]">
                            Bạn đã có tài khoản?{' '}
                            <Link to="/login" className="text-[#111827] font-[500] hover:underline">
                                Đăng nhập
                            </Link>
                        </span>
                    </div>
                </div>
            </div>

            <footer className="pb-6 px-6 flex flex-col items-center gap-4">
                <div className="flex flex-col items-center gap-1">
                    <span className="text-[13px] text-[#a0a0a5]">from</span>
                    <SimplizeOriginalLogo className="h-[18px] w-auto text-[#111827]" />
                </div>
                <div className="flex items-center gap-6 text-[13px] text-[#a0a0a5] text-center justify-center">
                    <a href="/terms" className="underline hover:text-[#111827] transition-colors">Điều khoản dịch vụ</a>
                    <a href="/privacy" className="underline hover:text-[#111827] transition-colors">Chính sách quyền riêng tư</a>
                    <span>&copy;{new Date().getFullYear()} Simplize</span>
                </div>
            </footer>
        </div>
    );
}
